import { useEffect, useState } from "react";
import "./Licence.css";
import { DashboardStats } from "../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import CustomInput from "../../../common/CustomInput";
import CustomSelect from "../../../common/CustomSelect";
import { Button } from "react-bootstrap";
import TopImage from "../../common/TopImage";
import { Images } from "../../../../config/images";
import BackButton from "../../../common/BackButton";
import { useGetSubSchoolDropDownList } from "../../../../hooks/organization/useGetSubSchoolDropDownList";
import { useTenantAuth } from "../../../../context/TeanantAuthProvider";
import { useAssignLicense } from "../../../../hooks/organization/useAssignLicense";
import SuccessModal from "../../../common/SuccessModal";
import { useNavigate } from "react-router-dom";
import CustomTextArea from "../../../common/CustomTextArea";
import TableError from "../../common/TableError";

const Licence = () => {
  const navigate = useNavigate();
  const { currentUser } = useTenantAuth();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Fetch school list from API
  const {
    subSchools,
    orgStats,
    loading: schoolsLoading,
    error: schoolsError,
    getSubSchoolDropDownList,
  } = useGetSubSchoolDropDownList();

  // Assign license hook
  const {
    loading: assignLoading,
    error: assignError,
    assignLicense,
  } = useAssignLicense();

  // Fetch school list on component mount
  useEffect(() => {
    getSubSchoolDropDownList({ orgId: currentUser?.school_id });
  }, []);

  // Stats data from API
  const stats = [
    {
      label: "Total Licenses Purchased",
      value: schoolsLoading
        ? "..."
        : (orgStats?.total_licenses ?? 0).toLocaleString(),
      icon: "fa-user",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Licenses Assigned",
      value: schoolsLoading
        ? "..."
        : (orgStats?.assigned_licenses ?? 0).toLocaleString(),
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Licenses Available",
      value: schoolsLoading
        ? "..."
        : (orgStats?.available_licenses ?? 0).toLocaleString(),
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-pastel-blue",
    },
  ];

  const availableLicenses = orgStats?.available_licenses ?? 0;
  const maxAssignLicenses = Math.min(availableLicenses, 10000);

  const validationSchema = Yup.object({
    schoolId: Yup.string().required("Please select a school"),
    addedLicenses: Yup.number()
      .required("Please enter number of licenses")
      .min(1, "At least 1 license is required")
      .max(
        maxAssignLicenses,
        availableLicenses > 10000
          ? "Cannot assign more than 10,000 licenses in a single request"
          : `Cannot add more than ${availableLicenses} licenses (available in organization)`,
      ),
    remarks: Yup.string().max(500, "Remarks cannot exceed 500 characters"),
  });

  const handleAssignLicense = async (values, { resetForm }) => {
    const licenseData = {
      sub_school_institution_id: parseInt(values.schoolId),
      license_count: parseInt(values.addedLicenses),
      remark: values.remarks,
    };

    await assignLicense(licenseData, () => {
      resetForm();
      setShowSuccessModal(true);
      // Refresh the school list to get updated license counts
      getSubSchoolDropDownList({ orgId: currentUser?.school_id });
    });
  };

  const formik = useFormik({
    initialValues: {
      schoolId: "",
      assignedLicenses: 0,
      addedLicenses: "",
      remarks: "",
    },
    validationSchema,
    onSubmit: handleAssignLicense,
  });

  // Update assignedLicenses when school selection changes
  const handleSchoolChange = (e) => {
    const selectedSchoolId = e.target.value;
    formik.setFieldValue("schoolId", selectedSchoolId);

    if (selectedSchoolId && subSchools) {
      const selectedSchool = subSchools.find(
        (school) => school.id.toString() === selectedSchoolId,
      );
      const assignedLicenses = selectedSchool?.license_assigned || 0;
      formik.setFieldValue("assignedLicenses", assignedLicenses);
    } else {
      formik.setFieldValue("assignedLicenses", 0);
    }
  };

  // Modal handlers
  const handleAssignMore = () => {
    setShowSuccessModal(false);
  };

  const handleGoToDashboard = () => {
    setShowSuccessModal(false);
    navigate("/organization/dashboard");
  };

  return (
    <div className="licence-cnt">
      <div className="pb-2">
        <BackButton />
      </div>
      {/* Top Banner Image */}
      <TopImage text="MANAGE LICENSES" imageSrc={Images.TopImage} />

      {/* Stats Cards */}
      <div className="container-fluid p-0">
        {schoolsLoading && !orgStats ? (
          <DashboardStatsSkeleton count={3} />
        ) : (
          <DashboardStats stats={stats} />
        )}
      </div>

      {/* Assign Licenses Form */}
      <div className="assign-license-section">
        <h3 className="section-title">ASSIGN LICENSES</h3>
        <div
          className="mb-4"
          style={{ backgroundColor: "#EEEFF7", height: "2px", width: "100%" }}
        />

        {/* Error State - Failed to fetch data */}
        {schoolsError && !schoolsLoading ? (
          <TableError
            error={schoolsError}
            handleRefresh={() =>
              getSubSchoolDropDownList({ orgId: currentUser?.school_id })
            }
            loading={schoolsLoading}
          />
        ) : !schoolsLoading && (!subSchools || subSchools.length === 0) ? (
          /* Empty State - No schools exist */
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ padding: "50px 20px" }}
          >
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                backgroundColor: "#cfebff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <i
                className="fa-solid fa-school"
                style={{ fontSize: "30px", color: "#014782" }}
              ></i>
            </div>
            <h5
              style={{
                color: "var(--text-color)",
                fontWeight: "700",
                fontSize: "18px",
                marginBottom: "8px",
              }}
            >
              No Schools Found
            </h5>
            <p
              style={{
                color: "#757575",
                fontSize: "14px",
                lineHeight: "1.6",
                marginBottom: "24px",
                textAlign: "center",
                maxWidth: "400px",
              }}
            >
              Please add a school first before you can assign licenses to it.
            </p>
            <Button
              className="custom-modal-primary-button"
              style={{ width: "auto", padding: "8px 24px" }}
              onClick={() => navigate("/organization/school/add-school")}
            >
              <i className="fa-solid fa-plus me-2"></i>
              Add School
            </Button>
          </div>
        ) : (
          /* Normal Form */
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <div className="row mb-4">
                <div className="col-12">
                  <CustomSelect
                    label="Select School"
                    name="schoolId"
                    onChange={handleSchoolChange}
                    disabled={schoolsLoading || assignLoading}
                  >
                    <option value="">
                      {schoolsLoading
                        ? "Loading schools..."
                        : "Choose school"}
                    </option>
                    {subSchools &&
                      subSchools.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name}
                        </option>
                      ))}
                  </CustomSelect>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-6">
                  <CustomInput
                    label="Licenses Assigned"
                    name="assignedLicenses"
                    type="text"
                    disabled
                    showAstreisk={false}
                    style={{ backgroundColor: "#F8F9FA" }}
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Add more Licenses"
                    name="addedLicenses"
                    type="number"
                    placeholder="Enter number of licenses"
                    disabled={assignLoading}
                  />
                  <div style={{
                    backgroundColor: "#e8f4fd",
                    border: "1px solid #b3d9f2",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    marginTop: "8px",
                    fontSize: "13px",
                    color: "#1a6fa8",
                  }}>
                    <i className="fa-solid fa-circle-info me-2"></i>
                    {schoolsLoading
                      ? "Checking available licenses..."
                      : `Available licenses in organization: ${availableLicenses} | Max per request: ${maxAssignLicenses}`}
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12">
                  <CustomTextArea
                    label="Remarks (Optional)"
                    name="remarks"
                    placeholder="Enter remarks"
                    showAstreisk={false}
                    disabled={assignLoading}
                  />
                </div>
              </div>

              {/* Error Display */}
              {assignError && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="custom-error">{assignError}</div>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-12">
                  <Button
                    type="submit"
                    className="custom-primary-button"
                    disabled={formik.isSubmitting || assignLoading}
                  >
                    {assignLoading ? "Assigning..." : "Assign Licenses"}
                  </Button>
                </div>
              </div>
            </form>
          </FormikProvider>
        )}
      </div>

      {/* Success Modal */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title="Licenses Assigned Successfully"
        message="The licenses have been successfully assigned to the selected school."
        primaryButtonText="Assign More"
        secondaryButtonText="Dashboard"
        onPrimaryClick={handleAssignMore}
        onSecondaryClick={handleGoToDashboard}
      />
    </div >
  );
};

export default Licence;
